"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { Menu, X, Instagram, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";

// Inline Facebook SVG so we get the real brand blue
function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.13 4.388 23.19 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.234 2.686.234v2.97h-1.513c-1.491 0-1.956.93-1.956 1.885v2.271h3.328l-.532 3.49h-2.796V24C19.612 23.19 24 18.13 24 12.073z" />
    </svg>
  );
}

type NavLink = {
  id: number;
  label: string;
  href: string;
  order_index: number;
  visible: boolean;
};

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navLinks, setNavLinks] = useState<NavLink[]>([]);
  const pathname = usePathname();

  // Pages that start with a dark/image hero
  const isDarkHero = pathname === "/" || pathname === "/centar-za-mlade";
  
  // If we are scrolled, or if we are on a page with a light background at the top
  const useDarkStyles = scrolled || !isDarkHero;

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 50); };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    fetch("/api/admin/navigation")
      .then((r) => r.json())
      .then((data) => {
        const visible = (data.links || []).filter((l: NavLink) => l.visible);
        setNavLinks(visible);
      })
      .catch(() => {
        // Fall back to default links on error
        setNavLinks([
          { id: 1, label: "Projekti", href: "/projekti", order_index: 0, visible: true },
          { id: 2, label: "Doniraj", href: "/doniraj", order_index: 1, visible: true },
          { id: 3, label: "Baci ideju", href: "/baci-ideju", order_index: 2, visible: true },
        ]);
      });
  }, []);

  // Last CTA link (last nav item used as primary CTA)
  const ctaLink = navLinks[navLinks.length - 1];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${useDarkStyles
        ? "bg-background/95 backdrop-blur-md border-b border-border"
        : "bg-transparent"
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center">
          <Image
            src="/images/kolektiv-logo.png"
            alt="Kolektiv - udruga mladih"
            width={220}
            height={90}
            className="h-20 md:h-24 w-auto transition-all duration-300"
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.id}
              href={link.href}
              className={`text-sm font-medium transition-colors ${useDarkStyles
                ? "text-muted-foreground hover:text-foreground relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-emerald-500 after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform"
                : "text-white/80 hover:text-white relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-white after:scale-x-0 hover:after:scale-x-100 after:origin-left after:transition-transform"
                }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Social + CTA */}
        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center gap-2">
            <a
              href="https://www.instagram.com/udruga_kolektiv/"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${scrolled
                ? "bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 text-white hover:scale-110"
                : "bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 text-white hover:scale-110"
                }`}
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              href="https://www.facebook.com/p/Udruga-mladih-Kolektiv-100064620704511/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full flex items-center justify-center bg-[#1877F2] text-white transition-all hover:scale-110 hover:bg-[#0d6efd]"
            >
              <FacebookIcon className="w-4 h-4" />
            </a>
            <a
              href="https://hr.linkedin.com/company/udruga-mladih-kolektiv"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full flex items-center justify-center bg-[#0A66C2] text-white transition-all hover:scale-110 hover:bg-[#084d90]"
            >
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
          {ctaLink && (
            <Button
              size="sm"
              asChild
              className={`rounded-full px-6 transition-all font-semibold ${useDarkStyles
                ? "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md shadow-emerald-500/25 border-0"
                : "bg-white text-emerald-700 hover:bg-white/90"
                }`}
            >
              <Link href={ctaLink.href}>{ctaLink.label}</Link>
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          className={`md:hidden transition-colors ${useDarkStyles ? "text-foreground" : "text-white"}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden bg-background border-b border-border">
          <nav className="flex flex-col px-6 py-6 gap-5">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 pt-4 border-t border-border">
              <a
                href="https://www.instagram.com/udruga_kolektiv/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 text-white hover:scale-110 transition-transform"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/p/Udruga-mladih-Kolektiv-100064620704511/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-[#1877F2] text-white hover:scale-110 transition-transform"
              >
                <FacebookIcon className="w-5 h-5" />
              </a>
              <a
                href="https://hr.linkedin.com/company/udruga-mladih-kolektiv"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-[#0A66C2] text-white hover:scale-110 transition-transform"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
            {ctaLink && (
              <Button size="sm" className="w-fit rounded-full px-6 mt-2" asChild>
                <Link href={ctaLink.href} onClick={() => setIsMenuOpen(false)}>{ctaLink.label}</Link>
              </Button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
